import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Activity, Clock, Award, Users, Filter } from 'lucide-react';
import { gamificationService } from '../services';
import PageContainer from '../components/PageContainer';
import EmptyState from '../components/EmptyState';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/Loading';
import { formatDistanceToNow } from '../utils/dateUtils';
import { getClassTypeIcon } from "../utils/characterTranslation";

const RecentActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    loadActivities();
  }, [filter, pagination.page]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filter !== 'all' && { type: filter }),
      };
      
      const response = await gamificationService.getRecentActivities(params);
      setActivities(response.activities || []);
      setPagination(response.pagination || {});
    } catch (err) {
      console.error('Error loading activities:', err);
      setError(err.response?.data?.error || 'Erro ao carregar atividades recentes');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getActivityIcon = (activity) => {
    const activityTypeIcons = {
      POINTS: '✨',
      EVENT_PARTICIPATION: '🗓️',
      CHARACTER_CREATED: '👤',
      LEVEL_POINTS_RECALCULATION: '🌀',
      LEVEL_UP: '🎆',
      QUEST_COMPLETION: '🎯',
      RELIC_ACQUISITION: '🪄',
    };

    return activityTypeIcons[activity.action] || '🔥';
  };

  const getActivityBadge = (activity) => {
    const activityTypeLabels = {
      POINTS: 'Pontos',
      EVENT_PARTICIPATION: 'Evento',
      CHARACTER_CREATED: 'Personagem',
      LEVEL_POINTS_RECALCULATION: 'Recálculo',
      LEVEL_UP: 'UP',
      QUEST_COMPLETION: 'Quest',
      RELIC_ACQUISITION: 'Relíquia',
    };
    
    return activityTypeLabels[activity.action] || 'Atividade';
  };

  const totalActivities = activities.length;
  const pointsActivities = activities.filter(a => a.action === 'POINTS').length;
  const eventsActivities = activities.filter(a => a.action === 'EVENT_PARTICIPATION').length;

  if (loading && activities.length === 0) {
    return (
      <PageContainer title="Atividades Recentes" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner text="Carregando atividades..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Atividades Recentes"
      description="Acompanhe as últimas atividades do clã em tempo real"
    >
      {/* Filters */}
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Filtrar por tipo</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filter === 'all' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('all')}
            >
              Todas
            </Button>
            <Button
              variant={filter === 'LEVEL_POINTS_RECALCULATION' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('LEVEL_POINTS_RECALCULATION')}
            >
              Recalculo de pontos
            </Button>
            <Button
              variant={filter === 'EVENT_PARTICIPATION' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('EVENT_PARTICIPATION')}
            >
              Eventos
            </Button>
            <Button
              variant={filter === 'LEVEL_UP' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('LEVEL_UP')}
            >
              Level UP
            </Button>
            <Button
              variant={filter === 'QUEST_COMPLETION' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('QUEST_COMPLETION')}
            >
              Quest
            </Button>
            <Button
              variant={filter === 'RELIC_ACQUISITION' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('RELIC_ACQUISITION')}
            >
              Relíquia
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/20 text-red-400 px-6 py-4 rounded-xl mb-6"
        >
          <div className="font-medium">{error}</div>
        </motion.div>
      )}

      {/* Activities Timeline */}
      {activities.length > 0 ? (
        <Card variant="glass">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Clock className="w-6 h-6 text-cyan-400" />
              Timeline de Atividades
            </h3>
            <div className="space-y-4">
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.id || index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-start gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-200 border border-white/5"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl">{getActivityIcon(activity)}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${
                          activity.action === 'POINTS' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                          activity.action === 'EVENT_PARTICIPATION' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                          'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        }`}>
                          {getActivityBadge(activity)}
                        </span>
                        {activity.character && (
                          <span className="text-sm text-gray-400">
                            {getClassTypeIcon(activity.character.class)} {activity.character.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        {activity.points > 0 && (
                          <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-medium border border-emerald-500/30">
                            +{activity.points} pts
                          </span>
                        )}
                        <span className="text-xs">
                          {formatDistanceToNow(new Date(activity.createdAt))} atrás
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-300 mb-2">
                      {activity.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Card>
      ) : (
        !loading && (
          <EmptyState
            icon={Activity}
            title="Nenhuma atividade encontrada"
            description={
              filter === 'all' 
                ? 'Não há atividades recentes para exibir.'
                : `Não há atividades do tipo "${filter}" para exibir.`
            }
          />
        )
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Card variant="glass" className="mt-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Página {pagination.page} de {pagination.totalPages} 
                <span className="text-gray-500 ml-2">({pagination.total} atividades)</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev || loading}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext || loading}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {loading && activities.length > 0 && (
        <div className="flex justify-center mt-6">
          <LoadingSpinner text="Carregando..." />
        </div>
      )}
    </PageContainer>
  );
};

export default RecentActivities;